// One focus/visibility listener for the whole app.
//
// Before this, "refresh when the user comes back to the tab" was hand-rolled at
// each call site: the sidebar alone registered four separate window 'focus'
// handlers, useAccount registered a fifth plus a 'visibilitychange' handler, and
// Messages a sixth. Returning to the tab therefore fired every one of them at
// once, unthrottled — a brand alt-tabbing produced a burst of requests each
// time, including a full brand reload and a 500-row sample fetch.
//
// Two things fix that, and both have to live in one place to work:
//
//  1. Coalescing. A tab return fires 'focus' AND 'visibilitychange' back to
//     back, so every subscriber ran twice. They are treated as one event within
//     COALESCE_MS.
//  2. Per-subscriber throttling. Each subscriber declares the minimum interval
//     it is worth re-running at, and is skipped until that has elapsed. Cheap
//     badge counts can refresh often; a full account reload should not.

interface Subscriber {
  fn: () => void
  minIntervalMs: number
  lastRun: number
}

const subscribers = new Set<Subscriber>()

/** 'focus' and 'visibilitychange' both fire on a tab return. One event, not two. */
const COALESCE_MS = 300

/**
 * How long the user must actually have been away for their return to count.
 *
 * `focus`/`blur` fire on far more than tab switches: clicking into docked
 * DevTools, a Cmd-Tab that lands back immediately, an OS notification stealing
 * focus for a moment. None of those mean the data went stale, but each used to
 * start the revalidation clock — which is what made the network panel look
 * busy while the user was, by their own account, doing nothing.
 *
 * A real "I went away and came back" is seconds at minimum. Anything shorter is
 * treated as never having left.
 */
const MIN_AWAY_MS = 10_000

let lastEventAt = 0
let listening = false
/** When focus/visibility was last lost. null while the page holds focus. */
let awaySince: number | null = null

function onAppAway() {
  if (awaySince === null) awaySince = Date.now()
}

function onAppFocus() {
  // A blur-driven visibilitychange must not count as coming back.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    onAppAway()
    return
  }
  const now = Date.now()
  if (now - lastEventAt < COALESCE_MS) return
  lastEventAt = now

  // Never left, or barely. Nothing can have changed that matters.
  const away = awaySince === null ? 0 : now - awaySince
  awaySince = null
  if (away < MIN_AWAY_MS) return
  for (const sub of subscribers) {
    if (now - sub.lastRun < sub.minIntervalMs) continue
    sub.lastRun = now
    try { sub.fn() } catch { /* a failing subscriber must not stop the others */ }
  }
}

function startListening() {
  if (listening || typeof window === 'undefined') return
  listening = true
  window.addEventListener('focus', onAppFocus)
  window.addEventListener('blur', onAppAway)
  document.addEventListener('visibilitychange', onAppFocus)
}

function stopListening() {
  if (!listening || typeof window === 'undefined') return
  listening = false
  window.removeEventListener('focus', onAppFocus)
  window.removeEventListener('blur', onAppAway)
  document.removeEventListener('visibilitychange', onAppFocus)
}

/**
 * Run `fn` when the user returns to the tab, at most once per `minIntervalMs`.
 *
 * `lastRun` starts at subscribe time, so a subscriber that has just mounted (and
 * therefore just fetched) does not immediately refetch on the next focus.
 *
 * Returns an unsubscribe function.
 */
export function onAppRevalidate(fn: () => void, minIntervalMs: number): () => void {
  const sub: Subscriber = { fn, minIntervalMs, lastRun: Date.now() }
  subscribers.add(sub)
  startListening()
  return () => {
    subscribers.delete(sub)
    if (subscribers.size === 0) stopListening()
  }
}

/** Test seam — resets module state between cases. */
export function __resetRevalidateForTests(): void {
  subscribers.clear()
  lastEventAt = 0
  awaySince = null
  stopListening()
}
